import React from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { discussionService, Post } from '../../api/discussionService';
import Card from '../UI/Card';
import { theme } from '../../styles/theme';

// Use the same default avatar as in PostDetail
const DEFAULT_AVATAR = 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png';

const PostContainer = styled(Card)`
  margin-bottom: ${theme.spacing.lg};
`;

const PostHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.md};
`;

const PostTitle = styled.h2`
  margin: 0;
  font-size: ${theme.fontSizes.large};
`;

const ViewAllLink = styled(Link)`
  font-size: ${theme.fontSizes.small};
  color: ${theme.colors.primary};
`;

const PostAuthor = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${theme.spacing.md};
  
  img {
    width: 40px;
    height: 40px;
    border-radius: ${theme.radii.round};
    margin-right: ${theme.spacing.sm};
  }
  
  div {
    h3 {
      margin: 0;
      font-size: ${theme.fontSizes.medium};
    }
    
    span {
      font-size: ${theme.fontSizes.small};
      color: ${theme.colors.gray};
    }
  }
`;

const PostContent = styled.p`
  font-size: ${theme.fontSizes.medium};
  line-height: 1.6;
  margin-bottom: ${theme.spacing.md};
`;

const PostActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  color: ${theme.colors.gray};
  font-size: ${theme.fontSizes.small};
  
  button {
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: ${theme.spacing.xs};
    color: ${theme.colors.gray};
    padding: 0;
    
    &:hover {
      color: ${theme.colors.primary};
    }
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const LoadingContainer = styled.div`
  padding: ${theme.spacing.lg};
  text-align: center;
  color: ${theme.colors.gray};
`;

const ErrorContainer = styled.div`
  padding: ${theme.spacing.md};
  color: ${theme.colors.danger};
  text-align: center;
`;

const PostLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  
  &:hover h3 {
    color: ${theme.colors.primary};
  }
`;

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const RecentPost: React.FC = () => {
  // Fetch the most recent post from the discussion service
  const { data, isLoading, error } = useQuery({
    queryKey: ['communityRecentPost'],
    queryFn: () => discussionService.getPosts({ limit: 1 }),
  });

  // Create a simple fallback post if no data is available
  const createFallbackPost = (): Post => ({
    id: 1,
    title: 'Join the Conversation',
    content: 'Welcome to our community! Share your stories and connect with others on their faith journey.',
    user_id: 1,
    author_username: 'saltmarsh',
    author_display_name: 'SaltMarsh Team',
    author_avatar_url: DEFAULT_AVATAR,
    status: 'published',
    category: 'general',
    tags: ['welcome', 'community'],
    likes_count: 0,
    comments_count: 0,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });

  if (isLoading) {
    return (
      <PostContainer>
        <LoadingContainer>Loading community post...</LoadingContainer>
      </PostContainer>
    );
  }

  // Get the post data from the response
  const posts = data?.data?.posts || [];
  
  // Use a fallback post if there's an error or no posts are available
  if (error || !posts || posts.length === 0) {
    console.log('Using fallback post data:', { error, posts });
    const fallbackPost = createFallbackPost();
    
    return (
      <PostContainer>
        <PostHeader>
          <PostTitle>From the Community</PostTitle>
          <ViewAllLink to="/community">View All</ViewAllLink>
        </PostHeader>
        
        <PostAuthor>
          <img 
            src={fallbackPost.author_avatar_url || DEFAULT_AVATAR} 
            alt={fallbackPost.author_display_name} 
          />
          <div>
            <h3>{fallbackPost.author_display_name}</h3>
            <span>{formatDate(fallbackPost.created_at)}</span>
          </div>
        </PostAuthor>
        
        <PostContent>{fallbackPost.content}</PostContent>
        
        <PostActions>
          <button aria-label="Like post">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
            <span>0 Likes</span>
          </button>
          
          <div>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
            </svg>
            <span>0 Comments</span>
          </div>
        </PostActions>
      </PostContainer>
    );
  }

  // Get the first post from the array
  const post = posts[0];
  
  // Use proper name display format
  const authorName = post.author_display_name || post.author_username || 'User';

  return (
    <PostContainer>
      <PostHeader>
        <PostTitle>From the Community</PostTitle>
        <ViewAllLink to="/community">View All</ViewAllLink>
      </PostHeader>
      
      <PostLink to={`/community/posts/${post.id}`}>
        <PostAuthor>
          <img 
            src={post.author_avatar_url || DEFAULT_AVATAR} 
            alt={authorName} 
          />
          <div>
            <h3>{authorName}</h3>
            <span>{formatDate(post.created_at)}</span>
          </div>
        </PostAuthor>
        
        <h3>{post.title}</h3>
        
        <PostContent>
          {post.content.length > 200 
            ? `${post.content.substring(0, 200)}...` 
            : post.content
          }
        </PostContent>
      </PostLink>
      
      <PostActions>
        <button aria-label="Like post">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
          </svg>
          <span>{post.likes_count || 0} Likes</span>
        </button>
        
        <div>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
          </svg>
          <span>{post.comments_count || 0} Comments</span>
        </div>
      </PostActions>
    </PostContainer>
  );
};

export default RecentPost; 